import React, { useState, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';
import "./Register.css";

// Country codes options - same as Register component
const countryCodes = [
  { code: "+971", name: "UAE" },
  { code: "+966", name: "KSA" },
  { code: "+974", name: "Qatar" },
  { code: "+965", name: "Kuwait" },
  { code: "+973", name: "Bahrain" },
  { code: "+968", name: "Oman" },
  { code: "+91", name: "India" },
  { code: "+92", name: "Pakistan" },
  { code: "+93", name: "Afghanistan" },
  { code: "+20", name: "Egypt" },
  { code: "+1", name: "USA/Canada" },
  { code: "+44", name: "UK" },
  { code: "+61", name: "Australia" },
  { code: "+65", name: "Singapore" },
  { code: "+60", name: "Malaysia" },
  { code: "+63", name: "Philippines" },
  { code: "+84", name: "Vietnam" },
  { code: "+62", name: "Indonesia" },
  { code: "+95", name: "Myanmar" },
  { code: "+855", name: "Cambodia" },
  { code: "+856", name: "Laos" },
  { code: "+66", name: "Thailand" },
  { code: "+880", name: "Bangladesh" },
  { code: "+94", name: "Sri Lanka" },
  { code: "+977", name: "Nepal" }
];

// Function to validate phone number input (allow only numbers)
const validatePhoneNumber = (value) => {
  // Remove any non-digit characters
  const numericValue = value.replace(/\D/g, '');
  return numericValue;
};

// Capitalize first letter helper
const capitalizeFirstLetter = (value) => {
  if (!value) return value;

  const words = value.split(' ');
  const capitalizedWords = words.map(word => {
    if (word.length === 0) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  });

  return capitalizedWords.join(' ');
};

// Phone Input Component
const PhoneInputWithCountryCode = React.memo(({
  numberField,
  countryCodeField,
  placeholder,
  required = false,
  className = "form-control rounded-input",
  formData,
  onCountryCodeChange,
  onInputChange
}) => (
  <div className="row g-2 align-items-center">
    <div className="col-4">
      <select
        className="form-control-countrycode rounded-input-countrycode"
        value={formData[countryCodeField]}
        onChange={(e) => onCountryCodeChange(countryCodeField, e.target.value)}
        required={required}
      >
        <option value="">Select Code</option>
        {countryCodes.map((country) => (
          <option key={country.code} value={country.code}>
            {country.code} ({country.name})
          </option>
        ))}
      </select>
    </div>
    <div className="col-8">
      <input
        type="tel"
        name={numberField}
        className={className}
        placeholder={placeholder}
        value={formData[numberField] || ""}
        onChange={onInputChange}
        required={required}
        pattern="[0-9]*"
        inputMode="numeric"
        onKeyPress={(e) => {
          // Allow only numbers, backspace, delete, tab, arrow keys
          if (!/[0-9]/.test(e.key) && 
              e.key !== 'Backspace' && 
              e.key !== 'Delete' && 
              e.key !== 'Tab' && 
              e.key !== 'ArrowLeft' && 
              e.key !== 'ArrowRight' &&
              e.key !== 'Home' &&
              e.key !== 'End') {
            e.preventDefault();
          }
        }}
      />
    </div>
  </div>
));

const HelperPlaceRegister = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    // Combined first & last name field
    first_name: "",
    mobile_number: "",
    mobile_number_country_code: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [registrationStep, setRegistrationStep] = useState(1); // 1: Basic info, 2: OTP verification

  // Use useCallback to memoize the handleChange function
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    let processedValue = value;
    
    // Apply validation for phone number fields
    if (name.includes('mobile_number')) {
      // Validate and clean phone number input
      processedValue = validatePhoneNumber(value);
    } else if (name === "first_name") {
      processedValue = capitalizeFirstLetter(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value.trim())) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  }, []);

  // Use useCallback to memoize the country code change handler
  const handleCountryCodeChange = useCallback((fieldName, countryCode) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: countryCode
    }));
  }, []);

  const sendOtp = async () => {
    if (emailError || !formData.email) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Email',
        text: 'Please enter a valid email address first',
        confirmButtonColor: '#f27474'
      });
      return;
    }

    // Validate basic fields
    if (!formData.first_name || !formData.password || !formData.confirmPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete Form',
        text: 'Please fill all required fields',
        confirmButtonColor: '#f27474'
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'Oops!',
        text: 'Passwords do not match',
        confirmButtonColor: '#f27474'
      });
      return;
    }

    try {
      setOtpLoading(true);

      const response = await fetch(`${BaseUrl}/send-reg-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: formData.email })
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.message || data.error || 'Failed to send OTP');

      Swal.fire({
        icon: 'success',
        title: 'OTP Sent!',
        text: 'Please check your email for the verification code',
        confirmButtonColor: '#28a745'
      });

      setOtpSent(true);
      setRegistrationStep(2); // Move to OTP verification step
    } catch (error) {
      console.error("Error sending OTP:", error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to Send OTP',
        text: error.message || 'Please try again later.',
        confirmButtonColor: '#d33'
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setOtpError("Please enter a valid OTP");
      return;
    }

    try {
      setOtpLoading(true);

      const response = await fetch(`${BaseUrl}/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          otp: otp
        })
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.message || data.error || 'OTP verification failed');

      Swal.fire({
        icon: 'success',
        title: 'OTP Verified!',
        text: 'Your email has been successfully verified',
        confirmButtonColor: '#28a745'
      });

      setOtpVerified(true);
      setOtpError("");
      await handleRegistration(); // Proceed with registration
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setOtpError(error.message || "Invalid OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleRegistration = async () => {
    if (formData.password !== formData.confirmPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'Oops!',
        text: 'Passwords do not match',
        confirmButtonColor: '#f27474'
      });
      return;
    }

    // Split the combined name into first and last name
    const nameParts = formData.first_name.trim().split(' ');
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(' ') || "";

    const payload = {
      first_name: firstName,
      last_name: lastName,
      mobile_number: formData.mobile_number || "",
      mobile_number_country_code: formData.mobile_number_country_code || '+971',
      email: formData.email,
      password: formData.password,
      source: "Direct",
      role: "job seeker",
      is_email_verified: otpVerified
    };

    try {
      setLoading(true);

      const response = await fetch(`${BaseUrl}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        Swal.fire({
          icon: 'error',
          title: 'Registration Failed',
          text: data.message || data.error || 'Registration failed. Please try again later.',
          confirmButtonColor: '#d33'
        });
        return;
      }

      // Auto login after successful registration
      const loginRes = await fetch(`${BaseUrl}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });

      const loginData = await loginRes.json();

      if (!loginRes.ok) {
        throw new Error(loginData.message || "Auto login failed");
      }

      // Save user to localStorage
      localStorage.setItem("user", JSON.stringify(loginData.user));

      Swal.fire({
        icon: 'success',
        title: 'Registration & Login Successful!',
        text: `Welcome, ${loginData.user.first_name || 'User'}!`,
        confirmButtonColor: '#28a745'
      }).then(() => {
        navigate("/viewjobs");
        window.location.reload(); // Optional: Refresh to update app state
      });

      // Reset form
      setFormData({
        first_name: "",
        mobile_number: "",
        mobile_number_country_code: "",
        email: "",
        password: "",
        confirmPassword: "",
      });

      // Reset OTP state
      setOtp("");
      setOtpSent(false);
      setOtpVerified(false);
      setRegistrationStep(1);

    } catch (error) {
      console.error("Error registering or logging in:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Please try again later.',
        confirmButtonColor: '#d33'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (registrationStep === 1) {
      // First step - validate basic info and send OTP
      await sendOtp();
    } else if (registrationStep === 2) {
      // OTP verification step
      await verifyOtp();
    }
  };

  const renderOtpVerification = () => (
    <div className="mb-4">
      <h6 className="fw-semibold border-bottom pb-2">Email Verification</h6>
      <div className="alert alert-info small mb-3">
        We've sent a 4-digit verification code to <strong>{formData.email}</strong>.
        Please enter it below to verify your email address.
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control rounded-input"
          placeholder="Enter 4-digit verification code"
          value={otp}
          onChange={(e) => {
            setOtp(e.target.value);
            setOtpError("");
          }}
          maxLength="4"
          required
        />
        {otpError && (
          <div className="invalid-feedback d-block">{otpError}</div>
        )}
      </div>

      <div className="text-center text-md-start">
        <div className="mb-2 mb-md-0">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={sendOtp}
            disabled={otpLoading}
          >
            {otpLoading ? "Resending..." : "Resend OTP"}
          </button>
        </div>
        <div className="small text-muted">
          Didn't receive code? Check spam folder
        </div>
      </div>
    </div>
  );

  const renderBasicInfoForm = () => (
    <>
      <div className="mb-2">
        <input
          type="text"
          name="first_name"
          className="form-control-register rounded-input"
          placeholder="Type your first & last name"
          value={formData.first_name}
          onChange={handleChange}
          required
        />
      </div>

      <div className="mb-2">
        <PhoneInputWithCountryCode
          numberField="mobile_number"
          countryCodeField="mobile_number_country_code"
          placeholder="Enter Mobile number"
          className="form-control-register rounded-input"
          formData={formData}
          onCountryCodeChange={handleCountryCodeChange}
          onInputChange={handleChange}
        />
      </div>

      <div className="mb-2">
        <input
          type="email"
          name="email"
          className={`form-control-register rounded-input ${emailError ? 'is-invalid' : ''}`}
          placeholder="Type your Email address"
          value={formData.email}
          onChange={handleChange}
          required
          disabled={otpSent}
        />
        {emailError && (
          <div className="invalid-feedback d-block">{emailError}</div>
        )}
      </div>

      <div className="mb-2 position-relative">
        <input
          type={showPassword ? "text" : "password"}
          name="password"
          className="form-control-register rounded-input pe-5"
          placeholder="Create password (minimum 8 characters)"
          value={formData.password}
          onChange={handleChange}
          required
        />
        <i
          className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"} position-absolute`}
          style={{
            top: "10px",
            right: "15px",
            cursor: "pointer",
            color: "#6c757d"
          }}
          onClick={() => setShowPassword((prev) => !prev)}
        ></i>
      </div>

      <div className="mb-3 position-relative">
        <input
          type={showConfirmPassword ? "text" : "password"}
          name="confirmPassword"
          className="form-control-register rounded-input pe-5"
          placeholder="Reconfirm your password"
          value={formData.confirmPassword}
          onChange={handleChange}
          required
        />
        <i
          className={`bi ${showConfirmPassword ? "bi-eye-slash" : "bi-eye"} position-absolute`}
          style={{
            top: "10px",
            right: "15px",
            cursor: "pointer",
            color: "#6c757d"
          }}
          onClick={() => setShowConfirmPassword((prev) => !prev)}
        ></i>
      </div>

      <div className="mb-3 form-check d-flex align-items-center">
        <input type="checkbox" className="form-check-input me-2" id="robotCheck" required />
        <label className="form-check-label small" htmlFor="robotCheck">
          I'm not a robot
        </label>
      </div>

      <div className="text-center small text-muted mb-2">
        By clicking Sign Up, you agree to the Gulf Worker <a href="#">Terms & Conditions</a> and <a href="#">Privacy Policy</a>.
      </div>
    </>
  );

  const getSubmitButtonText = () => {
    if (loading) return "Processing...";
    if (otpLoading) return "Verifying...";

    switch (registrationStep) {
      case 1:
        return "Send Verification Code";
      case 2:
        return "Verify OTP";
      default:
        return "SIGN UP";
    }
  };

  const goBackToBasicInfo = () => {
    setRegistrationStep(1);
    setOtp("");
    setOtpError("");
  };

  return (
    <div className="d-flex justify-content-center align-items-start bg-white py-5">
      <div className="card p-4 border-0 shadow-sm" style={{ maxWidth: "400px", width: "100%", marginTop: "33px" }}>
        <h6 className="mb-3 fw-semibold">Register as Helper</h6>

        <div className="rounded px-3 py-2 mb-3 small" style={{ backgroundColor: '#e9ecf1', color: 'black' }}>
          Register on Gulfworker.net to find job opportunities as a domestic helper
        </div>

        {registrationStep > 1 && (
          <div className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <div className={`rounded-circle ${registrationStep >= 1 ? 'bg-primary text-white' : 'bg-light text-muted'}`}
                style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                1
              </div>
              <div className={`ms-2 ${registrationStep >= 1 ? 'fw-semibold' : 'text-muted'}`}>Basic Information</div>
              <div className="flex-grow-1 mx-2 border-top"></div>

              <div className={`rounded-circle ${registrationStep >= 2 ? 'bg-primary text-white' : 'bg-light text-muted'}`}
                style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                2
              </div>
              <div className={`ms-2 ${registrationStep >= 2 ? 'fw-semibold' : 'text-muted'}`}>Email Verification</div>
            </div>

            {registrationStep === 2 && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mb-3"
                onClick={goBackToBasicInfo}
              >
                <FaArrowLeft className="me-1" />
                Back to Basic Info
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {registrationStep === 1 ? renderBasicInfoForm() : renderOtpVerification()}

          <button 
            type="submit" 
            className="btn w-100 d-flex justify-content-center align-items-center py-2 registration-submit-button"
            disabled={loading || otpLoading}
          >
            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <span style={{ fontSize: "17px" }}>
                {getSubmitButtonText()}
              </span>
              {!(loading || otpLoading) && (
                <span style={{ fontSize: "28px", lineHeight: "1", marginBottom: "4px" }}>
                  &#8250;
                </span>
              )}
            </div>
          </button>
        </form>

        {registrationStep === 1 && (
          <div className="text-center mt-3">
            <div style={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
              <span className="text-muted">Already have an account? </span>
              <span
                onClick={() => navigate('/login')}
                className="text-success text-decoration-none fw-semibold"
                style={{ cursor: 'pointer' }}
              >
                Login
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelperPlaceRegister;
